import { FC, MouseEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SpaceKind } from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  GlobeIcon,
  LockIcon,
  SlashCircleIcon,
  SuccessCircleIcon,
  VaultFolderIcon,
  WorkspaceFolderIcon,
} from '../../../../assets/icons/index';
import LoaderIcon from '../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  WORKSPACE_CARD_KIND_LABEL,
  WORKSPACE_CARD_STATUS_CLASS,
  WORKSPACE_CARD_STATUS_LABEL,
} from '../../constants/workspace-card.const';
import { WorkspaceCardMenuItem } from '../../constants/workspace-card-menu.const';
import {
  WorkspaceCardMenuAction,
  WorkspaceCardStat,
  WorkspaceCardStatus,
} from '../../types/data-room.types';
import { WorkspaceCardMenu } from './workspace-card-menu';

const DESCRIPTION_TRANSITION_MS = 200;

export type WorkspaceCardProps = {
  className?: string;
  description: ReactNode;
  icon?: ReactNode;
  // The space's archive is being packed: a pill says so until the zip is saved, and the menu stays.
  isDownloading?: boolean;
  kind: SpaceKind;
  lastUpdated: ReactNode;
  // Only for a card whose menu is not the owner's; left out, the kind decides it.
  menuItems?: WorkspaceCardMenuItem[];
  onClick?: () => void;
  onMenuAction?: (action: WorkspaceCardMenuAction) => void;
  showMenu?: boolean;
  stats: WorkspaceCardStat[];
  status?: WorkspaceCardStatus;
  title: ReactNode;
};

const WorkspaceCardStatusIcon: FC<{ status: WorkspaceCardStatus }> = ({ status }) => {
  if (status === WorkspaceCardStatus.LOCKED) {
    return <LockIcon className="size-3" height={12} iconColor="#DC6803" width={12} />;
  }

  if (status === WorkspaceCardStatus.ACTIVE) {
    return <SuccessCircleIcon iconColor="#039855" />;
  }

  return <SlashCircleIcon className="size-3" height={12} iconColor="#344054" width={12} />;
};

const WorkspaceCardKindIcon: FC<{ kind: SpaceKind }> = ({ kind }) => {
  if (kind === SpaceKind.VAULT) {
    return <VaultFolderIcon className="size-8" height={32} iconColor="#475467" width={32} />;
  }

  if (kind === SpaceKind.WORKSPACE) {
    return <WorkspaceFolderIcon />;
  }

  return <GlobeIcon />;
};

export const WorkspaceCard: FC<WorkspaceCardProps> = ({
  className,
  description,
  icon,
  isDownloading = false,
  kind,
  lastUpdated,
  menuItems,
  onClick,
  onMenuAction,
  showMenu = false,
  stats,
  status,
  title,
}) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const heightBeforeToggle = useRef<null | number>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  // Only a description the line actually cuts off gets the link; whether it does depends on the
  // card's width, so it is measured, not guessed from the length.
  useLayoutEffect(() => {
    const element = descriptionRef.current;

    if (!element || isExpanded) return;

    const measure = () => setIsTruncated(element.scrollWidth > element.clientWidth);

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [description, isExpanded]);

  // The text changes between one cut-off line and its full height at once; the height is walked
  // from the old value to the new so the card grows and folds instead of jumping.
  useLayoutEffect(() => {
    const element = descriptionRef.current;
    const from = heightBeforeToggle.current;

    heightBeforeToggle.current = null;

    if (!element || from === null) return;

    const to = element.offsetHeight;

    if (from === to) return;

    element.style.height = `${from}px`;
    // Reading the height commits the start value before the end value is set.
    void element.offsetHeight;
    element.style.height = `${to}px`;

    const timer = setTimeout(() => {
      element.style.height = '';
    }, DESCRIPTION_TRANSITION_MS);

    return () => {
      clearTimeout(timer);
      element.style.height = '';
    };
  }, [isExpanded]);

  useEffect(() => setIsExpanded(false), [description]);

  const handleToggleDescription = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    heightBeforeToggle.current = descriptionRef.current?.offsetHeight ?? null;
    setIsExpanded((expanded) => !expanded);
  };

  return (
    <article
      className={twMerge(
        'flex w-full max-w-[418px] flex-col overflow-hidden rounded-lg bg-white py-3 shadow-sm',
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
    >
      <div className="flex flex-col gap-4 px-3">
        <div className="flex flex-col gap-3">
          <div className="flex h-[22px] items-center justify-between">
            <p className="text-label-sm font-medium text-gray-400">
              <AppFormattedMessage id={WORKSPACE_CARD_KIND_LABEL[kind]} />
            </p>
            <div className="flex items-center gap-3">
              {isDownloading && (
                <div className="flex items-center gap-1 rounded-2xl bg-brand-50 px-2 py-0.5 text-brand-700">
                  <LoaderIcon className="size-3" height={12} width={12} />
                  <span className="text-label-md font-medium">
                    <AppFormattedMessage id="PREPARING_ARCHIVE" />
                  </span>
                </div>
              )}
              {status && (
                <div
                  className={twMerge(
                    'flex items-center gap-1 rounded-2xl px-2 py-0.5',
                    WORKSPACE_CARD_STATUS_CLASS[status],
                  )}
                >
                  <WorkspaceCardStatusIcon status={status} />
                  <span className="text-label-md font-medium">
                    <AppFormattedMessage id={WORKSPACE_CARD_STATUS_LABEL[status]} />
                  </span>
                </div>
              )}
              {showMenu && (
                <WorkspaceCardMenu items={menuItems} kind={kind} onAction={onMenuAction} />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {icon ?? <WorkspaceCardKindIcon kind={kind} />}
            <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
              <p className="w-full truncate text-sm font-[450] leading-5 text-gray-700">{title}</p>
              <div className="flex w-full items-end gap-2">
                {/* Not twMerge: it takes text-label-sm for a second colour and drops the size. */}
                <p
                  className={`min-w-0 flex-1 overflow-hidden text-left text-label-sm font-medium text-gray-500 transition-[height] duration-200 ease-out motion-reduce:transition-none ${
                    isExpanded ? '[overflow-wrap:anywhere]' : 'truncate'
                  }`}
                  ref={descriptionRef}
                >
                  {description}
                </p>
                {(isTruncated || isExpanded) && (
                  <button
                    aria-expanded={isExpanded}
                    className="shrink-0 text-label-sm font-medium text-brand-700 underline"
                    onClick={handleToggleDescription}
                    type="button"
                  >
                    <AppFormattedMessage id={isExpanded ? 'SEE_LESS' : 'SEE_MORE'} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center text-center">
          {stats.map((stat, index) => (
            <div
              className={twMerge(
                'flex min-w-px flex-1 flex-col items-start justify-center',
                index < stats.length - 1 && 'border-r border-gray-100',
              )}
              key={stat.label}
            >
              <p className="w-full text-sm font-[550] leading-5 text-gray-700">
                {stat.value}
                {stat.unit && (
                  <>
                    {' '}
                    <span className="text-label-xs font-semibold text-gray-400">
                      <AppFormattedMessage id={stat.unit} />
                    </span>
                  </>
                )}
              </p>
              <p className="w-full text-label-sm font-medium text-gray-400">
                <AppFormattedMessage id={stat.label} />
              </p>
            </div>
          ))}
        </div>
        <p className="text-label-sm font-medium text-gray-400">
          <AppFormattedMessage id="LAST_UPDATED" values={{ time: lastUpdated }} />
        </p>
      </div>
    </article>
  );
};
